// src/hooks/useFixPlacesDropdownAboveKeyboard.js
//
// Google Places Autocomplete injects `.pac-container` at document.body
// level and positions it BELOW the focused input using page-level
// coordinates from `window.innerHeight` — which does NOT shrink when
// the on-screen keyboard opens. Result: on mobile, the dropdown ends
// up behind the OSK and only the top row peeks out.
//
// Google's widget offers no configuration to change this. The fix has
// to intercept the DOM element and reposition it ourselves.
//
// This hook watches `.pac-container` and, on mobile (viewport < 768px),
// forces it ABOVE the currently focused input every time Google
// re-positions it (which happens on every keystroke as suggestions
// re-render).

import { useEffect } from 'react';

const MOBILE_BREAKPOINT = 768;
const GAP_ABOVE_INPUT = 6;

export function useFixPlacesDropdownAboveKeyboard() {
  useEffect(() => {
    if (typeof window === 'undefined' || !window.MutationObserver) return;

    const repositionAllPacs = () => {
      if (window.innerWidth >= MOBILE_BREAKPOINT) return;
      const pacs = document.querySelectorAll('.pac-container');
      if (pacs.length === 0) return;
      const focused = document.activeElement;
      if (!focused || focused.tagName !== 'INPUT') return;
      const rect = focused.getBoundingClientRect();
      pacs.forEach((pac) => {
        if (!pac.offsetHeight) return; // hidden / not measured yet
        const pacHeight = pac.offsetHeight;
        const inputTopPage = window.scrollY + rect.top;
        // Position dropdown just ABOVE the input.
        const newTop = Math.max(0, inputTopPage - pacHeight - GAP_ABOVE_INPUT);
        if (String(pac.style.top) !== `${newTop}px`) {
          pac.style.top = `${newTop}px`;
        }
      });
    };

    // Poll-lite: check on scroll + visualViewport resize + a MutationObserver
    // on the body subtree. The observer catches Google's per-keystroke style
    // resets (Google re-writes .style.top as suggestions arrive).
    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((m) => {
        const target = m.target;
        return target && target.classList && target.classList.contains('pac-container');
      });
      if (relevant) repositionAllPacs();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    const onVv = () => repositionAllPacs();
    window.addEventListener('scroll', onVv, true);
    window.visualViewport?.addEventListener('resize', onVv);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onVv, true);
      window.visualViewport?.removeEventListener('resize', onVv);
    };
  }, []);
}
